import React, { useState, useEffect } from 'react';
import { Brain, AlertCircle, TrendingUp, Edit3, CheckCircle, ExternalLink, RefreshCw, ChevronDown, ChevronUp, Sparkles, Lightbulb, Target, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const AssessmentResponseAnalyzer = ({ userId, onAnalysisComplete }) => {
  const navigate = useNavigate();
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responseQuality, setResponseQuality] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    insight1: true,
    insight2: false,
    insight3: false
  });

  useEffect(() => {
    if (userId) {
      analyzeAssessmentResponses();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const analyzeAssessmentResponses = async () => {
    try {
      setLoading(true);
      setError(null);

      // First check if we have cached analysis
      const cachedAnalysis = await loadCachedResponseAnalysis();
      if (cachedAnalysis) {
        setAnalysisData(cachedAnalysis);
        setResponseQuality(cachedAnalysis.responseQuality || 65);
        if (onAnalysisComplete) {
          onAnalysisComplete(cachedAnalysis);
        }
        return;
      }

      // If no cache, generate new analysis
      const freshAnalysis = await callResponseAnalysisAPI(userId);
      if (freshAnalysis) {
        setAnalysisData(freshAnalysis);
        setResponseQuality(freshAnalysis.responseQuality || 65);
        if (onAnalysisComplete) {
          onAnalysisComplete(freshAnalysis);
        }
      }
    } catch (error) {
      console.error('Error analyzing assessment responses:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCachedResponseAnalysis = async () => {
    try {
      const { data: assessmentData, error } = await supabase
        .from('assessment_responses')
        .select('response_analysis, response_analysis_created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !assessmentData?.response_analysis) {
        return null;
      }

      return assessmentData.response_analysis;
    } catch (error) {
      console.error('Error loading cached response analysis:', error);
      return null;
    }
  };

  const callResponseAnalysisAPI = async (userId) => {
    try {
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'assessment-response-analysis',
          userId: userId
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.analysis;
    } catch (error) {
      console.error('Response Analysis API Error:', error);
      throw error;
    }
  };

  const handleReviseAnswer = (questionId) => {
    // Navigate to assessment with specific question highlighted
    navigate(`/college/assessment?question=${questionId}&mode=revision`);
  };

  const handleRetakeAssessment = () => {
    navigate('/college/assessment?mode=improve');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Loading state
  if (!userId) {
    return null;
  }

  if (!analysisData && !loading && !error) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-8 text-center">
          <Brain className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-700 mb-2">Complete your strategic assessment</p>
          <p className="text-slate-500 text-sm mb-4">We need your assessment responses to provide personalized feedback</p>
          <button
            onClick={() => navigate('/college/assessment')}
            className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Take Assessment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      {/* Header - Dark Anchor Block */}
      <div className="bg-slate-900 text-white px-8 py-6 border-b-2 border-slate-900">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1 mt-1">
                <div className="w-2 h-2 rounded-full bg-coral-400"></div>
                <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                <div className="w-2 h-2 rounded-full bg-teal-400"></div>
              </div>
              <div>
                <h1 className="text-xl font-bold mb-1">Assessment Response Analysis</h1>
                <p className="text-sm text-slate-300">Your results, key truths, and deep dive</p>
              </div>
            </div>
            <div className="text-right">
              {loading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
              ) : error ? (
                <div className="text-red-400 text-sm">Error loading</div>
              ) : (
                <>
                  <div className="text-3xl font-bold flex items-center gap-2">
                    {responseQuality}%
                    <Sparkles size={20} className="text-yellow-400" />
                  </div>
                  <div className="text-xs text-slate-400">Ready to build</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {analysisData && (
        <div className="max-w-5xl mx-auto px-8 py-8">
          <div className="space-y-6">
            {/* Key Insight Card */}
            {analysisData.keyInsight && (
              <div className="bg-white border-2 border-slate-900 rounded-xl p-6 relative overflow-hidden">
                {/* Gradient accent bar */}
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-coral-400 via-yellow-500 to-teal-400"></div>

                <div className="flex items-start gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Key Insight</h2>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {analysisData.keyInsight}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* What Makes You Stand Out */}
            {analysisData.standoutSection && (
              <div className="bg-white border border-slate-900 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-base font-bold text-slate-900">What Makes You Stand Out</h2>
                </div>

                {/* Distinctive Pattern - Collapsible */}
                {analysisData.standoutSection.distinctivePattern && (
                  <div className="mb-4">
                    <button
                      onClick={() => toggleSection('insight1')}
                      className="w-full flex items-center justify-between text-left py-2 border-b border-stone-200 hover:border-yellow-300 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                        <span className="text-sm font-bold text-slate-900">Your Distinctive Pattern</span>
                      </div>
                      {expandedSections.insight1 ? <ChevronUp size={16} className="text-slate-600" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </button>
                    {expandedSections.insight1 && (
                      <div className="pt-4 pb-2 text-sm text-slate-700 leading-relaxed pl-4 ml-1">
                        <p>{analysisData.standoutSection.distinctivePattern}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Compelling Story - Collapsible */}
                {analysisData.standoutSection.compellingStory && (
                  <div className="mb-4">
                    <button
                      onClick={() => toggleSection('insight2')}
                      className="w-full flex items-center justify-between text-left py-2 border-b border-stone-200 hover:border-coral-300 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-coral-500"></div>
                        <span className="text-sm font-bold text-slate-900">The Story I Want to Know More About</span>
                      </div>
                      {expandedSections.insight2 ? <ChevronUp size={16} className="text-slate-600" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </button>
                    {expandedSections.insight2 && (
                      <div className="pt-4 pb-2 text-sm text-slate-700 leading-relaxed pl-4 ml-1">
                        <p>{analysisData.standoutSection.compellingStory}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Help Opportunities - Collapsible */}
                {analysisData.standoutSection.helpOpportunities && analysisData.standoutSection.helpOpportunities.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={() => toggleSection('insight3')}
                      className="w-full flex items-center justify-between text-left py-2 border-b border-stone-200 hover:border-teal-300 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                        <span className="text-sm font-bold text-slate-900">Here's What I Can Do With More Details</span>
                      </div>
                      {expandedSections.insight3 ? <ChevronUp size={16} className="text-slate-600" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </button>
                    {expandedSections.insight3 && (
                      <div className="pt-4 pb-2 text-sm text-slate-700 leading-relaxed pl-4 ml-1">
                        <ul className="space-y-2">
                          <li className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full bg-teal-500 mt-1.5 flex-shrink-0"></div>
                            <span>Once you add the specifics I'm curious about, I can help you:</span>
                          </li>
                          {analysisData.standoutSection.helpOpportunities.map((opportunity, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <div className="w-1 h-1 rounded-full bg-teal-500 mt-1.5 flex-shrink-0"></div>
                              <span>{opportunity}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Cards Grid - Quick Wins */}
            {analysisData.quickWins && analysisData.quickWins.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {analysisData.quickWins.map((win, index) => {
                  const colors = [
                    { bg: 'bg-coral-50', border: 'border-l-coral-600', text: 'text-coral-900', textSecondary: 'text-coral-800', icon: 'text-coral-700' },
                    { bg: 'bg-yellow-50', border: 'border-l-yellow-600', text: 'text-yellow-900', textSecondary: 'text-yellow-800', icon: 'text-yellow-700' },
                    { bg: 'bg-teal-50', border: 'border-l-teal-600', text: 'text-teal-900', textSecondary: 'text-teal-800', icon: 'text-teal-700' }
                  ];
                  const colorScheme = colors[index % 3];
                  const icons = [Lightbulb, Target, TrendingUp];
                  const Icon = icons[index % 3];

                  return (
                    <div key={index} className={`${colorScheme.bg} border-l-4 ${colorScheme.border} rounded-lg p-4 relative`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Icon size={14} className={colorScheme.icon} />
                        <h3 className={`text-xs font-bold ${colorScheme.text} uppercase tracking-wide`}>{win.title}</h3>
                      </div>
                      <p className={`text-xs ${colorScheme.textSecondary} leading-relaxed`}>
                        {win.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Response Analysis Section */}
            {analysisData.responses && analysisData.responses.length > 0 && (
              <div className="bg-white border border-slate-300 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-base font-bold text-slate-900">Response Analysis</h2>
                </div>
                <p className="text-xs text-stone-600 mb-6">Here's what information is missing from your key responses</p>

                {analysisData.responses.map((response, index) => (
                  <div key={index} className="border border-slate-300 rounded-lg mb-4 overflow-hidden">
                    {/* Response Header */}
                    <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
                      <span className="text-sm font-bold">{response.questionTitle}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-400"></div>
                        <span className="text-xs bg-slate-700 px-2 py-1 rounded">
                          {response.improvementScore || 'Strong'}
                        </span>
                      </div>
                    </div>

                    <div className="p-4">
                      {/* Student's Answer Quote */}
                      <p className="text-xs text-slate-700 italic mb-4 border-l-2 border-stone-300 pl-3">
                        "{response.studentAnswer}"
                      </p>

                      {/* Three-Column Analysis */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* What I See */}
                        <div className="bg-yellow-50 border-l-2 border-l-yellow-500 rounded p-3 relative">
                          <div className="flex items-start gap-2 mb-1">
                            <Lightbulb size={14} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                            <span className="text-xs font-bold text-yellow-900">What I See</span>
                          </div>
                          <p className="text-xs text-yellow-800 leading-relaxed">
                            {response.whatISee}
                          </p>
                        </div>

                        {/* What I Need to Know */}
                        <div className="bg-coral-50 border-l-2 border-l-coral-500 rounded p-3 relative">
                          <div className="flex items-start gap-2 mb-1">
                            <AlertCircle size={14} className="text-coral-600 mt-0.5 flex-shrink-0" />
                            <span className="text-xs font-bold text-coral-900">What I Need to Know</span>
                          </div>
                          <p className="text-xs text-coral-800 leading-relaxed">
                            {response.whatINeedToKnow}
                          </p>
                        </div>

                        {/* Help Me Understand */}
                        <div className="bg-teal-50 border-l-2 border-l-teal-500 rounded p-3 relative">
                          <div className="flex items-start gap-2 mb-1">
                            <CheckCircle size={14} className="text-teal-600 mt-0.5 flex-shrink-0" />
                            <span className="text-xs font-bold text-teal-900">Help Me Understand</span>
                          </div>
                          <p className="text-xs text-teal-800 leading-relaxed">
                            {response.helpMeUnderstand}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleRetakeAssessment}
                className="flex-1 bg-coral-500 text-white border-2 border-slate-900 rounded-lg px-6 py-3 text-sm font-bold hover:bg-coral-600 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-coral-500 to-coral-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <span className="relative flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                  Open Assessment and Add More Details
                  <ArrowRight size={16} />
                </span>
              </button>

              {responseQuality > 70 && (
                <button
                  onClick={() => navigate('/college/essays?tab=ideas&mode=insights&source=strategic-insights')}
                  className="flex-1 bg-teal-600 text-white border-2 border-slate-900 rounded-lg px-6 py-3 text-sm font-bold hover:bg-teal-700 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <span className="relative flex items-center gap-2">
                    <Sparkles size={16} />
                    Generate Strategic Essay Topics
                    <ArrowRight size={16} />
                  </span>
                </button>
              )}
            </div>

            {/* Show upgrade message if quality is too low */}
            {responseQuality <= 70 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <div className="text-yellow-800 font-semibold mb-2">
                  🎯 Unlock Essay Topic Generation
                </div>
                <div className="text-yellow-700 text-sm">
                  Reach 70%+ response quality to access AI-powered essay topic generation based on your strategic insights
                </div>
                <div className="mt-2 text-yellow-600 text-xs">
                  Current: {responseQuality}% • Target: 70%+
                </div>
              </div>
            )}

            {/* Bottom Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* How This Analysis Helps You */}
              <div className="bg-white border border-slate-300 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-bold text-slate-900">How This Analysis Helps You</h3>
                </div>
                <ul className="space-y-2 text-xs text-slate-700">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-coral-500 mt-1.5 flex-shrink-0"></div>
                    <span>Identifies your unique strengths and compelling storylines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-coral-500 mt-1.5 flex-shrink-0"></div>
                    <span>Reveals gaps in your narrative where added details make a difference</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-coral-500 mt-1.5 flex-shrink-0"></div>
                    <span>Generates personalized essay topics when quality threshold is met</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-coral-500 mt-1.5 flex-shrink-0"></div>
                    <span>Creates strategic insights for college positioning</span>
                  </li>
                </ul>
              </div>

              {/* Response Quality Goals with Progress Bars */}
              <div className="bg-white border border-slate-300 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-bold text-slate-900">Response Quality Goals</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-700">Basic Insight</span>
                      <span className="text-xs font-bold text-slate-900">40%+</span>
                    </div>
                    <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-coral-400 to-coral-500" style={{width: '40%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-700">Detailed Analysis</span>
                      <span className="text-xs font-bold text-slate-900">70%+</span>
                    </div>
                    <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500" style={{width: '70%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-700">Essay Topic Generation</span>
                      <span className="text-xs font-bold text-slate-900">70%+</span>
                    </div>
                    <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-teal-400 to-teal-500" style={{width: '70%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-700">Strategic Positioning</span>
                      <span className="text-xs font-bold text-slate-900">80%+</span>
                    </div>
                    <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600" style={{width: Math.min(responseQuality, 100) + '%'}}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 mt-3 pt-3 border-t border-stone-200">
                  <p className="text-xs text-stone-600">
                    The more details you provide, the more personalized and strategic your guidance becomes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentResponseAnalyzer;